import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"
import { DEFERRED_LABELS, describeCounts, formatCentral, sumValues } from "../../lib/program"
import type { CapUsage, StateBreakdown, TickRecord } from "../../types/outreach-program.types"

export function NotAvailable({ what }: { what: string }) {
  return <p className="text-sm text-muted-foreground">{what}: not available</p>
}

function Meter({ label, used, cap }: { label: string; used: number; cap: number }) {
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className={cn("tabular-nums font-medium", pct >= 100 && "text-destructive", pct >= 80 && pct < 100 && "text-warning")}>
          {used} / {cap}
        </span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  )
}

/** Calls today vs the clinic cap; global texts/segments vs their caps; what the caps deferred. */
export function CapUsagePanel({ usage }: { usage: CapUsage | null | undefined }) {
  if (!usage) return <NotAvailable what="Cap usage" />
  const deferred = sumValues(usage.deferred_today)
  return (
    <div className="space-y-3" data-testid="cap-usage">
      <p className="text-xs text-muted-foreground">{usage.day} (Central)</p>
      <Meter label="Calls booked today (this clinic)" used={usage.calls_booked} cap={usage.max_calls_per_day} />
      <Meter label="Texts scheduled today (all clinics)" used={usage.texts_scheduled_global} cap={usage.max_texts_per_day} />
      <Meter label="Text segments today (all clinics)" used={usage.segments_scheduled_global} cap={usage.max_segments_per_day} />
      <p className="text-xs text-muted-foreground">
        Deferred today: {deferred === 0 ? "none" : describeCounts(usage.deferred_today, DEFERRED_LABELS)}
      </p>
    </div>
  )
}

const errorText = (e: Record<string, unknown>) =>
  String(e.error ?? e.message ?? e.detail ?? JSON.stringify(e))

export function LastTick({ tick }: { tick: TickRecord | null | undefined }) {
  if (!tick) return <p className="text-sm text-muted-foreground">No tick has run for this clinic yet.</p>
  return (
    <div className="space-y-1 text-sm" data-testid="last-tick">
      <p>
        <span className="text-muted-foreground">Last tick</span> {formatCentral(tick.finished_at ?? tick.started_at)} ·{" "}
        <span className="font-medium">{tick.mode}</span>
        {tick.manual_by && <span className="text-muted-foreground"> by {tick.manual_by}</span>}
      </p>
      {tick.summary && (
        <p className="text-xs text-muted-foreground">
          {tick.summary.booked} booked · {tick.summary.planned} planned
          {sumValues(tick.summary.deferred) > 0 && ` · deferred ${describeCounts(tick.summary.deferred, DEFERRED_LABELS)}`}
        </p>
      )}
      {tick.error_count > 0 ? (
        <div className="text-xs text-destructive">
          {tick.error_count} error{tick.error_count === 1 ? "" : "s"}
          <ul className="list-disc pl-4">
            {(tick.errors ?? []).slice(0, 5).map((e, i) => (
              <li key={i}>{errorText(e)}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-success">No errors</p>
      )}
    </div>
  )
}

export function TickTable({ ticks }: { ticks: TickRecord[] | null }) {
  if (!ticks) return <NotAvailable what="Recent ticks" />
  if (ticks.length === 0) return <p className="text-sm text-muted-foreground">No ticks yet.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-3 font-normal">Finished</th>
            <th className="py-1 pr-3 font-normal">Mode</th>
            <th className="py-1 pr-3 text-right font-normal">Booked</th>
            <th className="py-1 pr-3 text-right font-normal">Planned</th>
            <th className="py-1 pr-3 text-right font-normal">Deferred</th>
            <th className="py-1 pr-3 text-right font-normal">Errors</th>
          </tr>
        </thead>
        <tbody>
          {ticks.map((t) => (
            <tr key={t.id} className="border-t">
              <td className="py-1 pr-3">{formatCentral(t.finished_at ?? t.started_at)}</td>
              <td className="py-1 pr-3">
                {t.mode}
                {t.manual_by && <span className="text-muted-foreground"> ({t.manual_by})</span>}
              </td>
              <td className="py-1 pr-3 text-right tabular-nums">{t.summary?.booked ?? "-"}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{t.summary?.planned ?? "-"}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{t.summary ? sumValues(t.summary.deferred) : "-"}</td>
              <td className={cn("py-1 pr-3 text-right tabular-nums", t.error_count > 0 && "text-destructive")}>
                {t.error_count}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The program's per-state breakdown (active members) next to the pipeline's
 * own state counts. The two totals must match; a mismatch is flagged.
 */
export function StateBreakdownTable({
  breakdown,
  stateCounts,
}: {
  breakdown: StateBreakdown | null | undefined
  stateCounts: Record<string, number>
}) {
  if (!breakdown) return <NotAvailable what="Per-state breakdown" />
  const states = Object.keys(breakdown)
  if (states.length === 0) return <p className="text-sm text-muted-foreground">No active members.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="state-breakdown">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-3 font-normal">State</th>
            <th className="py-1 pr-3 text-right font-normal" title="Pipeline count (patient-lifecycle-summary)">Pipeline</th>
            <th className="py-1 pr-3 text-right font-normal">Total</th>
            <th className="py-1 pr-3 text-right font-normal">Booked</th>
            <th className="py-1 pr-3 text-right font-normal">Due now</th>
            <th className="py-1 pr-3 text-right font-normal">Waiting</th>
            <th className="py-1 pr-3 text-right font-normal">Capped</th>
            <th className="py-1 pr-3 font-normal">Blocked</th>
          </tr>
        </thead>
        <tbody>
          {states.map((state) => {
            const r = breakdown[state]
            const pipeline = stateCounts[state] ?? 0
            const mismatch = pipeline !== r.total
            return (
              <tr key={state} className="border-t">
                <td className="py-1 pr-3 font-mono text-[11px]">{state}</td>
                <td
                  className={cn("py-1 pr-3 text-right tabular-nums", mismatch && "text-destructive font-semibold")}
                  title={mismatch ? "Does not match the program's total" : undefined}
                >
                  {pipeline}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">{r.total}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{r.booked}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{r.due_now}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{r.waiting}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{r.capped}</td>
                <td className="py-1 pr-3 text-muted-foreground">{describeCounts(r.blocked) || "-"}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
